/** Standard municipal / default keys — used for warnings only, not blocking. */
export const KNOWN_SECTION_KEYS = [
  'scope',
  'technical',
  'eligibility',
  'commercial',
  'scopeOfWork',
  'eligibilityRequirements',
  'evaluationMethodology',
  'paymentTerms',
  'projectTimeline',
  'penaltyClause',
  'publishingAuthority',
];

export const BUILDER_SLOT_OPTIONS = [
  { value: 'introduction', label: 'Introduction' },
  { value: 'project_background', label: 'Project Background' },
  { value: 'eligibility_points', label: 'Eligibility (bullet list)' },
  { value: 'financial_bid_details', label: 'Financial Bid' },
  { value: 'document_overflow', label: 'Document overflow (append)' },
];

export const DEFAULT_MUNICIPAL_SECTIONS = [
  { key: 'scopeOfWork', label: 'Scope of Work', order: 1, builderSlot: 'introduction' },
  { key: 'eligibilityRequirements', label: 'Eligibility Requirements', order: 2, builderSlot: 'eligibility_points' },
  { key: 'evaluationMethodology', label: 'Evaluation Methodology', order: 3, builderSlot: 'project_background' },
  { key: 'paymentTerms', label: 'Payment Terms', order: 4, builderSlot: 'financial_bid_details' },
  { key: 'projectTimeline', label: 'Project Timeline', order: 5, builderSlot: 'project_background' },
  { key: 'penaltyClause', label: 'Penalty Clause', order: 6, builderSlot: 'financial_bid_details' },
  { key: 'publishingAuthority', label: 'Publishing Authority', order: 7, builderSlot: 'introduction' },
];

const LEGACY_KEY_BUILDER_SLOT = {
  scope: 'introduction',
  scopeOfWork: 'introduction',
  introduction: 'introduction',
  projectOverview: 'introduction',
  publishingAuthority: 'introduction',
  publishing: 'introduction',
  technical: 'project_background',
  projectBackground: 'project_background',
  background: 'project_background',
  evaluationMethodology: 'project_background',
  projectTimeline: 'project_background',
  timeline: 'project_background',
  eligibility: 'eligibility_points',
  eligibilityRequirements: 'eligibility_points',
  qualifyingRequirements: 'eligibility_points',
  commercial: 'financial_bid_details',
  financialBid: 'financial_bid_details',
  paymentTerms: 'financial_bid_details',
  penaltyClause: 'financial_bid_details',
  penalty: 'financial_bid_details',
};

export const CAMEL_CASE_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*$/;

export const humanizeSectionKey = (key = '') =>
  String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const VALID_BUILDER_SLOTS = new Set(BUILDER_SLOT_OPTIONS.map((o) => o.value));

export const inferBuilderSlotFromKey = (key = '') =>
  LEGACY_KEY_BUILDER_SLOT[String(key)] || 'document_overflow';

export const normalizeSectionDefinition = (entry, index = 0) => {
  if (typeof entry === 'string') {
    const key = String(entry).trim();
    if (!key) return null;
    return {
      key,
      label: humanizeSectionKey(key),
      order: index + 1,
      builderSlot: inferBuilderSlotFromKey(key),
    };
  }

  if (!entry || typeof entry !== 'object') return null;

  const key = String(entry.key || '').trim();
  if (!key) return null;

  const builderSlot = VALID_BUILDER_SLOTS.has(entry.builderSlot)
    ? entry.builderSlot
    : inferBuilderSlotFromKey(key);

  return {
    key,
    label: String(entry.label || '').trim() || humanizeSectionKey(key),
    order: Number.isFinite(Number(entry.order)) ? Number(entry.order) : index + 1,
    builderSlot,
  };
};

/** Normalize string[] or object[] section mapping into sorted definitions. */
export const resolveSectionDefinitions = (promptConfig) => {
  const raw = promptConfig?.sectionDefinitions?.length
    ? promptConfig.sectionDefinitions
    : promptConfig?.sectionMapping;

  if (!Array.isArray(raw) || !raw.length) return [];

  return raw
    .map((entry, index) => normalizeSectionDefinition(entry, index))
    .filter(Boolean)
    .sort((a, b) => a.order - b.order || a.key.localeCompare(b.key));
};

export const resolveSectionKeys = (promptConfig) =>
  resolveSectionDefinitions(promptConfig).map((d) => d.key);

export const parseSectionMappingInput = (text = '') =>
  String(text)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/** @deprecated use validateSectionDefinitions */
export const validateSectionMapping = (keys = []) => {
  const definitions = keys.map((key, index) =>
    normalizeSectionDefinition({ key, order: index + 1 }, index),
  );
  return validateSectionDefinitions(definitions);
};

export const validateSectionDefinitions = (sections = []) => {
  const errors = [];
  const warnings = [];
  const rawSections = sections || [];

  rawSections.forEach((entry, index) => {
    const key = String(entry?.key || '').trim();
    const label = String(entry?.label || '').trim();
    if (!key && !label) {
      errors.push(`Section row ${index + 1} is empty. Add a key and label, or remove the row.`);
      return;
    }
    if (!key) {
      errors.push(`Section row ${index + 1} is missing a camelCase key.`);
    }
    if (key && !label) {
      errors.push(`Section row ${index + 1} is missing a label.`);
    }
  });

  const normalized = rawSections
    .map((entry, index) => normalizeSectionDefinition(entry, index))
    .filter(Boolean);

  if (!normalized.length && !errors.length) {
    errors.push('At least one section is required.');
  }

  const seen = new Set();
  normalized.forEach((section) => {
    if (!CAMEL_CASE_KEY_PATTERN.test(section.key)) {
      errors.push(
        `"${section.key}" must be camelCase (e.g. scopeOfWork, paymentTerms).`,
      );
    }
    if (seen.has(section.key)) {
      errors.push(`Duplicate section key: "${section.key}".`);
    }
    seen.add(section.key);

    if (!section.label?.trim()) {
      errors.push(`Label is required for section "${section.key}".`);
    }

    if (!KNOWN_SECTION_KEYS.includes(section.key)) {
      warnings.push(
        `"${section.key}" is a custom section key. Set a builder slot so the EOI template maps correctly.`,
      );
    }
  });

  return {
    sections: normalized.map((section, index) => ({
      ...section,
      order: index + 1,
    })),
    valid: errors.length === 0,
    errors,
    warnings,
  };
};

export const formatSectionMappingSummary = (sectionMapping = []) => {
  const defs = resolveSectionDefinitions({ sectionMapping });
  if (!defs.length) return '-';
  return defs.map((d) => d.label || d.key).join(', ');
};

export const createEmptySectionRow = (order = 1) => ({
  key: '',
  label: '',
  order,
  builderSlot: 'document_overflow',
});

/** Roles allowed to create/edit/delete AI prompt configurations. */
const PROMPT_CONFIG_ADMIN_ROLES = new Set([
  'super_admin',
  'admin',
  'tenant_super_admin',
]);

export const canManageAiPromptConfig = (user) => {
  const roleName = String(user?.role?.name || user?.role || '')
    .toLowerCase()
    .trim();
  return PROMPT_CONFIG_ADMIN_ROLES.has(roleName);
};
